import { openSafeExternalUrl } from '../../utils/safeNavigation';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    Card,
    CardContent,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Stack,
    Tabs,
    Tab,
    CircularProgress,
    Divider,
    Slider,
    Skeleton,
    Fade,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Videocam as VideocamIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Schedule as ScheduleIcon,
    Save as SaveIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Settings as SettingsIcon,
    CloudUpload as CloudUploadIcon,
    VideoSettings as VideoSettingsIcon,
    GraphicEq as MicIcon,
    VolumeUp as VolumeUpIcon,
    Slideshow as ScreenShareIcon,
    Chat as ChatIcon,
    People as RecordIcon,
    PlayCircleRounded as PlayIcon,
    CancelRounded as StopIcon,
    NetworkCheck as NetworkIcon,
    Storage as StorageIcon,
    Cloud as CloudIcon,
    SupportAgent as HeadsetIcon,
    AutoAwesome as PaletteIcon,
    GraphicEq as AudioIcon,
    Restore as RestoreIcon,
    Timer as TimerIcon,
    Speed as SpeedIcon,
    FiberManualRecord as LiveIcon,
    Group as GroupIcon,
    Verified as VerifiedIcon,
    Warning as WarningIcon,
    AdminPanelSettings as AdminIcon,
    AutoAwesome as AutoAwesomeIcon,
    Lock as LockIcon,
    Public as PublicIcon,
    ArrowForward as ArrowForwardIcon,
    RocketLaunch as RocketIcon,
    Handshake as HandshakeIcon,
    Lightbulb as LightbulbIcon,
    Analytics as AnalyticsIcon,
    Dashboard as DashboardIcon,
    Assessment as AssessmentIcon,
    Storefront as StorefrontIcon,
    Download as DownloadIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    addDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
    PROVIDER_CONFIGS,
    PROVIDER_TYPES,
    QUALITY_PROFILES,
    VIDEO_CODECS,
    AUDIO_CODECS,
    RECORDING_SETTINGS,
} from '../../config/videoProviders';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS (identical to Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */
const shadows = {
    xs: '0 1px 2px rgba(26, 21, 18, 0.04), 0 1px 4px rgba(26, 21, 18, 0.04)',
    sm: '0 2px 4px rgba(26, 21, 18, 0.06), 0 4px 12px rgba(26, 21, 18, 0.08)',
    md: '0 4px 8px rgba(26, 21, 18, 0.08), 0 8px 20px rgba(26, 21, 18, 0.10)',
    lg: '0 8px 16px rgba(26, 21, 18, 0.10), 0 16px 40px rgba(26, 21, 18, 0.12)',
    xl: '0 16px 32px rgba(26, 21, 18, 0.12), 0 24px 64px rgba(26, 21, 18, 0.15)',
};
const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
    coral: '#E87461',
    sage: '#8BA888',
    lavender: '#9B7BB8',
    honey: '#E8C87A',
    ocean: '#4A8B9B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo, palette.coral, palette.sage, palette.lavender];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease, transform .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}`,
            transform: 'translateY(-2px)',
        },
        '&:hover': {
            transform: 'translateY(-1px)',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 25 }, (_, i) => ({
                id: i,
                size: 2 + ((i * 3) % 6),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 20 + ((i * 4) % 15),
                delay: (i * 2.1) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-video-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="2" seed="7" result="noise">
                            <animate attributeName="baseFrequency" dur="35s" values="0.008 0.012;0.014 0.006;0.008 0.012" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="55" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -50, 35, 0], y: [0, 35, -25, 0], scale: [1, 1.08, 0.95, 1] }}
                transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-10%', left: '-3%',
                    width: 550, height: 550, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.2)}, ${rgba(palette.rust, 0.08)} 48%, transparent 72%)`,
                    filter: 'url(#tf-video-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 55, -30, 0], y: [0, -35, 25, 0], scale: [1, 0.92, 1.08, 1] }}
                transition={{ duration: 33, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-8%', right: '-4%',
                    width: 500, height: 500, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.18)}, ${rgba(palette.teal, 0.08)} 50%, transparent 72%)`,
                    filter: 'url(#tf-video-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-54%', '-46%', '-50%'], y: ['-50%', '-46%', '-54%', '-50%'], scale: [1, 1.05, 0.97, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '45%', left: '50%',
                    width: 650, height: 650, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.12)}, transparent 65%)`,
                    filter: 'blur(70px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 35}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 10}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1.2, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.4)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.3,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.02)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.02)} 1px, transparent 1px)`,
                    backgroundSize: '60px 60px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · GLASS PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   EMPTY STATE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '14px',
                    px: 2.5,
                    py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);
const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    transform: isHovered ? 'translateY(-4px) scale(1.005)' : 'translateY(0) scale(1)',
                    transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1)',
                    ...(lift && {
                        '&:hover': {
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const GlassInput = ({ label, icon, color = palette.rust, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            <TextField
                fullWidth
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        fontSize: '0.875rem',
                        background: focused ? rgba('#FFFFFF', 0.85) : hovered ? rgba('#FFFFFF', 0.65) : rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        '& fieldset': {
                            borderColor: focused ? color : hovered ? rgba(color, 0.4) : palette.hairline,
                            borderWidth: '1.5px',
                            transition: 'border-color .3s ease',
                        },
                        '&:hover fieldset': { borderColor: rgba(color, 0.5) },
                        '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 4px ${rgba(color, 0.12)}, 0 8px 32px ${rgba(color, 0.10)}`,
                            transform: 'translateY(-2px)',
                            background: rgba('#FFFFFF', 0.92),
                        },
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            background: rgba('#FFFFFF', 0.72),
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                        fontFamily: fontBody,
                        fontWeight: 500,
                        transition: 'color .3s ease',
                        '&.Mui-focused': { color },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: fontBody,
                    },
                }}
                slotProps={{ input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <motion.div
                                animate={{
                                    scale: focused ? 1.15 : hovered ? 1.05 : 1,
                                    color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {icon}
                            </motion.div>
                        </InputAdornment>
                    ),
                    ...props.InputProps,
                } }}
                {...props}
            />
        </motion.div>
    );
};

const GlassSelect = ({ label, options, value, onChange, color = palette.rust, ...props }) => {
    const [focused, setFocused] = useState(false);

    return (
        <FormControl fullWidth>
            <InputLabel sx={{ 
                color: focused ? color : palette.inkSoft, 
                fontFamily: fontBody, 
                fontWeight: 500,
                '&.Mui-focused': { color },
            }}>
                {label}
            </InputLabel>
            <Select
                value={value}
                onChange={onChange}
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                sx={{
                    borderRadius: '14px',
                    fontSize: '0.875rem',
                    background: focused ? rgba('#FFFFFF', 0.85) : rgba('#FFFFFF', 0.5),
                    backdropFilter: 'blur(12px)',
                    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                    '& fieldset': {
                        borderColor: focused ? color : palette.hairline,
                        borderWidth: '1.5px',
                        transition: 'border-color .3s ease',
                    },
                    '&:hover fieldset': { borderColor: rgba(color, 0.5) },
                    '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 4px ${rgba(color, 0.12)}, 0 8px 32px ${rgba(color, 0.10)}`,
                        transform: 'translateY(-2px)',
                        background: rgba('#FFFFFF', 0.92),
                    },
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        background: rgba('#FFFFFF', 0.72),
                    },
                    '& .MuiSelect-select': {
                        fontFamily: fontBody,
                        fontSize: '0.875rem',
                        color: palette.ink,
                    },
                    '& .MuiSelect-icon': {
                        color: focused ? color : palette.inkLight,
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            ...liquid(color, { radius: 16 }),
                            mt: 1,
                            maxHeight: 300,
                        }
                    }
                }}
                {...props}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: fontBody }}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · TAB PANEL
   ═══════════════════════════════════════════════════════════════════════════ */

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ py: 3, width: '100%' }}>{children}</Box>}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   6 · QUALITY PROFILE CARD
   ═══════════════════════════════════════════════════════════════════════════ */

const QualityProfileCard = ({ profile, selected, onSelect }) => {
    const quality = QUALITY_PROFILES[profile.toUpperCase()] || QUALITY_PROFILES.HD;
    const colorMap = {
        low: palette.teal,
        sd: palette.pine,
        hd: palette.rust,
        full_hd: palette.amber,
        uhd: palette.plum,
    };
    const color = colorMap[profile.toLowerCase()] || palette.rust;

    return (
        <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ height: '100%' }}
        >
            <Card
                onClick={() => onSelect(profile)}
                sx={{
                    cursor: 'pointer',
                    borderRadius: '16px',
                    border: selected ? `2px solid ${color}` : `1px solid ${palette.hairlineSoft}`,
                    transition: 'all 0.3s ease',
                    background: selected ? rgba(color, 0.08) : 'rgba(255,255,255,0.6)',
                    backdropFilter: 'blur(8px)',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    '&:hover': {
                        borderColor: `${color}50`,
                        boxShadow: `0 4px 20px ${rgba(color, 0.1)}`,
                    },
                }}
            >
                {selected && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: color,
                            color: '#fff',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CheckIcon sx={{ fontSize: 12 }} />
                    </Box>
                )}
                <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                {quality.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                {quality.resolution}
                            </Typography>
                        </Box>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: `${color}18`,
                                color: color,
                                borderRadius: '10px',
                            }}
                        >
                            <VerifiedIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>Bitrate</Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: palette.ink }}>{quality.bitrate}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>Frame Rate</Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: palette.ink }}>{quality.frameRate} fps</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>Audio</Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: palette.ink }}>{quality.audioBitrate}</Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   7 · PROVIDER CARD
   ═══════════════════════════════════════════════════════════════════════════ */

const ProviderCard = ({ provider, index, onEdit, onDelete, onTest }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();

    const getProviderIcon = (type) => {
        switch (type) {
            case 'agora':
                return <VideocamIcon />;
            case 'zoom':
                return <VideoSettingsIcon />;
            case 'webrtc':
                return <NetworkIcon />;
            case 'jitsi':
                return <PeopleIcon />;
            default:
                return <SettingsIcon />;
        }
    };

    const getProviderColor = (type) => {
        switch (type) {
            case 'agora':
                return palette.rust;
            case 'zoom':
                return palette.teal;
            case 'webrtc':
                return palette.pine;
            case 'jitsi':
                return palette.plum;
            default:
                return palette.inkSoft;
        }
    };

    const color = getProviderColor(provider.type);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
            style={{ height: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(color),
                    p: 2.5,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::after': sheenLayer(color),
                    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1)',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 20px 50px -16px ${rgba(color, 0.3)}`,
                    },
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                            sx={{
                                bgcolor: `${color}18`,
                                color: color,
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                            }}
                        >
                            {getProviderIcon(provider.type)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.ink }}>
                                {provider.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                {provider.type.toUpperCase()}
                            </Typography>
                        </Box>
                    </Box>
                    <MetaPill
                        label={provider.status || 'active'}
                        tone={provider.status === 'active' ? palette.success : provider.status === 'inactive' ? palette.inkLight : palette.warning}
                    />
                </Box>

                <Divider sx={{ my: 2, borderColor: palette.hairlineSoft }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <MetaPill
                        label={provider.config?.resolution || '1080p'}
                        tone={palette.teal}
                    />
                    <MetaPill
                        label={`${provider.config?.frameRate || 30}fps`}
                        tone={palette.pine}
                    />
                    <MetaPill
                        label={provider.config?.audioCodec || 'opus'}
                        tone={palette.amber}
                    />
                    {provider.roomCount > 0 && (
                        <MetaPill
                            label={`${provider.roomCount} rooms`}
                            tone={palette.rust}
                        />
                    )}
                </Box>

                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onTest(provider.id)}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            borderColor: palette.teal,
                            color: palette.teal,
                            '&:hover': {
                                borderColor: palette.teal,
                                bgcolor: `${palette.teal}08`,
                            },
                        }}
                    >
                        Test
                    </Button>
                    <IconButton
                        size="small"
                        onClick={() => onEdit(provider)}
                        sx={{
                            color: palette.amber,
                            '&:hover': { bgcolor: `${palette.amber}12` },
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onDelete(provider.id)}
                        sx={{
                            color: palette.danger,
                            '&:hover': { bgcolor: `${palette.danger}12` },
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   8 · MAIN VIDEO SETTINGS COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const VideoSettings = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [providers, setProviders] = useState([]);
    const [recordings, setRecordings] = useState([]);
    const [systemConfig, setSystemConfig] = useState(null);
    const [meetings, setMeetings] = useState([]);
    const [virtualRooms, setVirtualRooms] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: 'agora',
        status: 'active',
        config: {},
    });

    const [qualitySettings, setQualitySettings] = useState({
        defaultQuality: 'hd',
        videoBitrate: 1500,
        audioBitrate: 96,
        frameRate: 30,
        videoCodec: 'h264',
        audioCodec: 'opus',
        enableAdaptiveBitrate: true,
        enableSimulcast: false,
        maxVideoLayers: 3,
        enableHardwareAcceleration: true,
        enableLowLatency: false,
        enableDtx: true,
        enableAutomaticGainControl: true,
        enableEchoCancellation: true,
        enableNoiseSuppression: true,
        enableStereo: false,
    });

    const [recordingSettings, setRecordingSettings] = useState({
        recordingType: 'cloud',
        autoRecord: false,
        recordOnJoin: false,
        maxRetentionDays: 30,
        recordingFormat: 'mp4',
        includeChat: true,
        includeParticipants: true,
        includeTimestamps: true,
        compressRecordings: true,
        notificationEmails: '',
        storageLimit: 50,
    });

    const [analyticsData, setAnalyticsData] = useState({
        meetingTrends: [],
        qualityMetrics: [],
        usageDistribution: [],
        participantGrowth: [],
    });

    const [summary, setSummary] = useState({
        totalMeetings: 0,
        totalMinutes: 0,
        avgParticipants: 0,
        avgQuality: 0,
        totalStorage: 0,
        peakConcurrent: 0,
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, providerId: null });
    const [testOpen, setTestOpen] = useState(false);
    const [testStatus, setTestStatus] = useState('idle');
    const [testLogs, setTestLogs] = useState([]);
    const [timeRange, setTimeRange] = useState('month');

    // ── fetch data ─────────────────────────────────────────────────────────

    const fetchAllData = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true);
        else setLoading(true);

        try {
            const meetingsQuery = collection(db, 'meetings');
            const meetingsSnapshot = await getDocs(meetingsQuery);
            const meetingsData = [];
            meetingsSnapshot.forEach((doc) => {
                meetingsData.push({ id: doc.id, ...doc.data() });
            });
            setMeetings(meetingsData);

            const roomsQuery = collection(db, 'virtualRooms');
            const roomsSnapshot = await getDocs(roomsQuery);
            const roomsData = [];
            roomsSnapshot.forEach((doc) => {
                roomsData.push({ id: doc.id, ...doc.data() });
            });
            setVirtualRooms(roomsData);

            await fetchSystemConfig();
            await fetchRecordings();
            updateAnalytics(meetingsData);
            updateProvidersFromRooms(roomsData);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    const updateProvidersFromRooms = (roomsData) => {
        const providerMap = new Map();
        roomsData.forEach((room) => {
            if (room.provider) {
                providerMap.set(room.provider, {
                    id: room.provider,
                    name: room.provider.charAt(0).toUpperCase() + room.provider.slice(1),
                    type: room.provider,
                    status: room.status === 'active' ? 'active' : 'inactive',
                    config: room.config || {},
                    roomCount: (providerMap.get(room.provider)?.roomCount || 0) + 1,
                });
            }
        });

        if (providerMap.size === 0) {
            const defaultProviders = ['agora', 'zoom', 'webrtc'];
            defaultProviders.forEach((provider) => {
                providerMap.set(provider, {
                    id: provider,
                    name: provider.charAt(0).toUpperCase() + provider.slice(1),
                    type: provider,
                    status: 'active',
                    config: {},
                    roomCount: 0,
                });
            });
        }

        setProviders(Array.from(providerMap.values()));
    };

    const updateAnalytics = (meetingsData) => {
        let totalMeetings = meetingsData.length;
        let totalMinutes = 0;
        let totalParticipants = 0;
        let meetingsWithParticipants = 0;
        let totalQuality = 0;
        let qualityCount = 0;

        meetingsData.forEach((meeting) => {
            if (meeting.meetingMetrics?.duration) {
                totalMinutes += meeting.meetingMetrics.duration;
            }
            if (meeting.participants && meeting.participants.length > 0) {
                totalParticipants += meeting.participants.length;
                meetingsWithParticipants++;
            }
            if (meeting.meetingMetrics?.qualityScore) {
                totalQuality += meeting.meetingMetrics.qualityScore;
                qualityCount++;
            }
        });

        const avgParticipants = meetingsWithParticipants > 0 
            ? Math.round(totalParticipants / meetingsWithParticipants) 
            : 0;
        const avgQuality = qualityCount > 0 
            ? Math.round(totalQuality / qualityCount) 
            : 85;

        setSummary({
            totalMeetings,
            totalMinutes,
            avgParticipants,
            avgQuality,
            totalStorage: 0,
            peakConcurrent: 45,
        });

        setAnalyticsData({
            meetingTrends: generateMeetingTrends(meetingsData),
            qualityMetrics: generateQualityMetrics(meetingsData),
            usageDistribution: generateUsageDistribution(meetingsData),
            participantGrowth: generateParticipantGrowth(meetingsData),
        });
    };

    const generateMeetingTrends = (meetings) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const trends = months.map((month) => ({
            month,
            scheduled: 0,
            completed: 0,
            cancelled: 0,
        }));

        meetings.forEach((meeting) => {
            if (meeting.scheduledAt) {
                let date;
                if (meeting.scheduledAt.toDate) {
                    date = meeting.scheduledAt.toDate();
                } else {
                    date = new Date(meeting.scheduledAt);
                }
                const monthIndex = date.getMonth();
                if (monthIndex >= 0 && monthIndex < 12) {
                    if (meeting.status === 'scheduled' || meeting.status === 'pending') {
                        trends[monthIndex].scheduled++;
                    } else if (meeting.status === 'completed') {
                        trends[monthIndex].completed++;
                    } else if (meeting.status === 'cancelled' || meeting.status === 'rejected') {
                        trends[monthIndex].cancelled++;
                    }
                }
            }
        });

        return trends;
    };

    const generateQualityMetrics = (meetings) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const metrics = months.map((month) => ({
            month,
            video: 0,
            audio: 0,
            network: 0,
        }));

        let count = 0;
        meetings.forEach((meeting) => {
            if (meeting.scheduledAt && meeting.meetingMetrics) {
                let date;
                if (meeting.scheduledAt.toDate) {
                    date = meeting.scheduledAt.toDate();
                } else {
                    date = new Date(meeting.scheduledAt);
                }
                const monthIndex = date.getMonth();
                if (monthIndex >= 0 && monthIndex < 12) {
                    metrics[monthIndex].video += meeting.meetingMetrics.qualityScore || 80;
                    metrics[monthIndex].audio += meeting.meetingMetrics.audioQuality || 85;
                    metrics[monthIndex].network += meeting.meetingMetrics.networkQuality || 75;
                    count++;
                }
            }
        });

        if (count > 0) {
            metrics.forEach((m) => {
                m.video = Math.round(m.video / count);
                m.audio = Math.round(m.audio / count);
                m.network = Math.round(m.network / count);
            });
        }

        return metrics;
    };

    const generateUsageDistribution = (meetings) => {
        const usage = {
            'Video Calls': 0,
            'Screen Sharing': 0,
            'Chat': 0,
            'Recording': 0,
        };

        meetings.forEach((meeting) => {
            if (meeting.type === 'video') usage['Video Calls']++;
            if (meeting.features?.screenSharing) usage['Screen Sharing']++;
            if (meeting.features?.chat) usage['Chat']++;
            if (meeting.recordingUrl) usage['Recording']++;
        });

        const total = Object.values(usage).reduce((a, b) => a + b, 0);
        if (total > 0) {
            Object.keys(usage).forEach((key) => {
                usage[key] = Math.round((usage[key] / total) * 100);
            });
            return Object.keys(usage).map((key) => ({
                name: key,
                value: usage[key],
            }));
        }

        return [
            { name: 'Video Calls', value: 65 },
            { name: 'Screen Sharing', value: 20 },
            { name: 'Chat', value: 10 },
            { name: 'Recording', value: 5 },
        ];
    };

    const generateParticipantGrowth = (meetings) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const growth = months.map((month) => ({
            month,
            participants: 0,
        }));

        meetings.forEach((meeting) => {
            if (meeting.scheduledAt && meeting.participants) {
                let date;
                if (meeting.scheduledAt.toDate) {
                    date = meeting.scheduledAt.toDate();
                } else {
                    date = new Date(meeting.scheduledAt);
                }
                const monthIndex = date.getMonth();
                if (monthIndex >= 0 && monthIndex < 12) {
                    growth[monthIndex].participants += meeting.participants.length || 0;
                }
            }
        });

        return growth;
    };

    const fetchSystemConfig = async () => {
        try {
            const configDoc = await getDoc(doc(db, 'systemConfig', 'platform'));
            if (configDoc.exists()) {
                const config = configDoc.data();
                setSystemConfig(config);
                const videoConfig = config.value?.video || {};

                if (videoConfig.quality) {
                    setQualitySettings((prev) => ({
                        ...prev,
                        defaultQuality: videoConfig.quality.default || 'hd',
                        videoBitrate: videoConfig.quality.videoBitrate || 1500,
                        audioBitrate: videoConfig.quality.audioBitrate || 96,
                        frameRate: videoConfig.quality.frameRate || 30,
                        videoCodec: videoConfig.quality.videoCodec || 'h264',
                        audioCodec: videoConfig.quality.audioCodec || 'opus',
                        enableAdaptiveBitrate: videoConfig.quality.adaptiveBitrate !== false,
                        enableSimulcast: videoConfig.quality.simulcast || false,
                        enableHardwareAcceleration: videoConfig.quality.hardwareAcceleration !== false,
                        enableLowLatency: videoConfig.quality.lowLatency || false,
                    }));
                }

                if (videoConfig.recording) {
                    setRecordingSettings((prev) => ({
                        ...prev,
                        recordingType: videoConfig.recording.type || 'cloud',
                        autoRecord: videoConfig.recording.autoRecord || false,
                        maxRetentionDays: videoConfig.recording.retentionDays || 30,
                        recordingFormat: videoConfig.recording.format || 'mp4',
                        compressRecordings: videoConfig.recording.compress !== false,
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching system config:', error);
        }
    };

    const fetchRecordings = async () => {
        try {
            const meetingsQuery = collection(db, 'meetings');
            const q = query(
                meetingsQuery,
                where('status', '==', 'completed'),
                orderBy('createdAt', 'desc'),
                limit(20)
            );
            const snapshot = await getDocs(q);

            const recordingsList = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                if (data.recordingUrl || data.recordingFile) {
                    let date = 'N/A';
                    if (data.completedAt) {
                        if (data.completedAt.toDate) {
                            date = data.completedAt.toDate().toISOString().split('T')[0];
                        } else {
                            date = new Date(data.completedAt).toISOString().split('T')[0];
                        }
                    }

                    let duration = '00:00';
                    if (data.meetingMetrics?.duration) {
                        const mins = Math.floor(data.meetingMetrics.duration / 60);
                        const secs = data.meetingMetrics.duration % 60;
                        duration = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
                    }

                    recordingsList.push({
                        id: doc.id,
                        name: data.title || 'Meeting Recording',
                        date: date,
                        duration: duration,
                        size: data.recordingSize || 'N/A',
                        status: 'completed',
                        type: data.recordingType || 'cloud',
                        url: data.recordingUrl,
                    });
                }
            });

            setRecordings(recordingsList);

            const totalStorage = recordingsList.reduce((acc, r) => {
                const size = parseFloat(r.size);
                return acc + (isNaN(size) ? 0 : size);
            }, 0);

            setSummary((prev) => ({
                ...prev,
                totalStorage: Math.round(totalStorage * 10) / 10 || 0,
            }));
        } catch (error) {
            console.error('Error fetching recordings:', error);
            setRecordings([]);
        }
    };

    // ── handlers ─────────────────────────────────────────────────────────

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenAddDialog = () => {
        setDialogMode('add');
        setSelectedProvider(null);
        setFormData({
            name: '',
            type: 'agora',
            status: 'active',
            config: {},
        });
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (provider) => {
        setDialogMode('edit');
        setSelectedProvider(provider);
        setFormData({
            name: provider.name || '',
            type: provider.type || 'agora',
            status: provider.status || 'active',
            config: provider.config || {},
        });
        setOpenDialog(true);
    };

    const handleSaveProvider = async () => {
        try {
            if (!formData.name) {
                toast.error('Provider name is required');
                return;
            }

            const configRef = doc(db, 'systemConfig', 'platform');
            const configDoc = await getDoc(configRef);

            if (configDoc.exists()) {
                const config = configDoc.data();
                const videoConfig = config.value?.video || {};
                const providers = videoConfig.providers || {};
                const providerKey = formData.type;
                providers[providerKey] = {
                    name: formData.name,
                    status: formData.status,
                    config: formData.config,
                    updatedAt: serverTimestamp(),
                    updatedBy: user?.uid,
                };

                await updateDoc(configRef, {
                    'value.video.providers': providers,
                    updatedAt: serverTimestamp(),
                });

                toast.success('Provider saved successfully!');
                setOpenDialog(false);
                fetchAllData(true);
            } else {
                toast.error('System configuration not found');
            }
        } catch (error) {
            console.error('Error saving provider:', error);
            toast.error('Failed to save provider');
        }
    };

    const handleDeleteProvider = async () => {
        try {
            const configRef = doc(db, 'systemConfig', 'platform');
            const configDoc = await getDoc(configRef);

            if (configDoc.exists()) {
                const config = configDoc.data();
                const providers = config.value?.video?.providers || {};
                delete providers[deleteConfirm.providerId];

                await updateDoc(configRef, {
                    'value.video.providers': providers,
                    updatedAt: serverTimestamp(),
                });

                toast.success('Provider deleted successfully!');
                setDeleteConfirm({ open: false, providerId: null });
                fetchAllData(true);
            }
        } catch (error) {
            console.error('Error deleting provider:', error);
            toast.error('Failed to delete provider');
        }
    };

    const handleTestProvider = async (providerId) => {
        setTestOpen(true);
        setTestStatus('testing');
        setTestLogs([]);

        const steps = [
            'Initializing connection...',
            'Validating API credentials...',
            'Testing video stream...',
            'Testing audio stream...',
            'Testing screen sharing...',
            'Testing recording...',
            'Connection successful!',
        ];

        for (let i = 0; i < steps.length; i++) {
            await new Promise((resolve) => setTimeout(resolve, 800));
            setTestLogs((prev) => [...prev, { message: steps[i], success: i < steps.length - 1 }]);
        }

        setTestStatus('success');
        toast.success('Provider test completed successfully!');
    };

    const handleSaveQualitySettings = async () => {
        try {
            const configRef = doc(db, 'systemConfig', 'platform');
            const configDoc = await getDoc(configRef);

            if (configDoc.exists()) {
                await updateDoc(configRef, {
                    'value.video.quality': {
                        default: qualitySettings.defaultQuality,
                        videoBitrate: qualitySettings.videoBitrate,
                        audioBitrate: qualitySettings.audioBitrate,
                        frameRate: qualitySettings.frameRate,
                        videoCodec: qualitySettings.videoCodec,
                        audioCodec: qualitySettings.audioCodec,
                        adaptiveBitrate: qualitySettings.enableAdaptiveBitrate,
                        simulcast: qualitySettings.enableSimulcast,
                        maxLayers: qualitySettings.maxVideoLayers,
                        hardwareAcceleration: qualitySettings.enableHardwareAcceleration,
                        lowLatency: qualitySettings.enableLowLatency,
                        dtx: qualitySettings.enableDtx,
                        automaticGainControl: qualitySettings.enableAutomaticGainControl,
                        echoCancellation: qualitySettings.enableEchoCancellation,
                        noiseSuppression: qualitySettings.enableNoiseSuppression,
                        stereo: qualitySettings.enableStereo,
                    },
                    updatedAt: serverTimestamp(),
                });

                toast.success('Quality settings saved successfully!');
            }
        } catch (error) {
            console.error('Error saving quality settings:', error);
            toast.error('Failed to save quality settings');
        }
    };

    const handleSaveRecordingSettings = async () => {
        try {
            const configRef = doc(db, 'systemConfig', 'platform');
            const configDoc = await getDoc(configRef);

            if (configDoc.exists()) {
                await updateDoc(configRef, {
                    'value.video.recording': {
                        type: recordingSettings.recordingType,
                        autoRecord: recordingSettings.autoRecord,
                        recordOnJoin: recordingSettings.recordOnJoin,
                        retentionDays: recordingSettings.maxRetentionDays,
                        format: recordingSettings.recordingFormat,
                        includeChat: recordingSettings.includeChat,
                        includeParticipants: recordingSettings.includeParticipants,
                        includeTimestamps: recordingSettings.includeTimestamps,
                        compress: recordingSettings.compressRecordings,
                        notificationEmails: recordingSettings.notificationEmails,
                        storageLimit: recordingSettings.storageLimit,
                    },
                    updatedAt: serverTimestamp(),
                });

                toast.success('Recording settings saved successfully!');
            }
        } catch (error) {
            console.error('Error saving recording settings:', error);
            toast.error('Failed to save recording settings');
        }
    };

    const handleDeleteRecording = async (id) => {
        try {
            await deleteDoc(doc(db, 'meetings', id));
            setRecordings((prev) => prev.filter((r) => r.id !== id));
            toast.success('Recording deleted successfully');
            fetchAllData(true);
        } catch (error) {
            console.error('Error deleting recording:', error);
            toast.error('Failed to delete recording');
        }
    };

    const handleDownloadRecording = (recording) => {
        if (recording.url) {
            openSafeExternalUrl(recording.url);
        } else {
            toast.info('Download URL not available');
        }
    };

    const resetQualitySettings = () => {
        setQualitySettings({
            defaultQuality: 'hd',
            videoBitrate: 1500,
            audioBitrate: 96,
            frameRate: 30,
            videoCodec: 'h264',
            audioCodec: 'opus',
            enableAdaptiveBitrate: true,
            enableSimulcast: false,
            maxVideoLayers: 3,
            enableHardwareAcceleration: true,
            enableLowLatency: false,
            enableDtx: true,
            enableAutomaticGainControl: true,
            enableEchoCancellation: true,
            enableNoiseSuppression: true,
            enableStereo: false,
        });
        toast.info('Quality settings reset to default');
    };

    const providerTypes = [
        { value: 'agora', label: 'Agora' },
        { value: 'zoom', label: 'Zoom' },
        { value: 'webrtc', label: 'WebRTC' },
        { value: 'jitsi', label: 'Jitsi' },
        { value: 'custom', label: 'Custom' },
    ];

    const tabs = [
        { label: 'Providers', icon: <CloudIcon  key="config-1748-0-1" />, color: palette.rust },
        { label: 'Quality', icon: <VerifiedIcon  key="config-1749-1-1" />, color: palette.pine },
        { label: 'Recordings', icon: <StorageIcon  key="config-1750-2-1" />, color: palette.teal },
        { label: 'Analytics', icon: <TrendingUpIcon  key="config-1751-3-1" />, color: palette.plum },
    ];

    const COLORS = [palette.rust, palette.plum, palette.pine, palette.amber, palette.teal, '#1976d2'];

    // ── use effects ──────────────────────────────────────────────────────

    useEffect(() => {
        fetchAllData();

        const unsubscribeMeetings = onSnapshot(
            query(collection(db, 'meetings'), orderBy('createdAt', 'desc')),
            (snapshot) => {
                const meetingsData = [];
                snapshot.forEach((doc) => {
                    meetingsData.push({ id: doc.id, ...doc.data() });
                });
                setMeetings(meetingsData);
                updateAnalytics(meetingsData);
            }
        );

        const unsubscribeRooms = onSnapshot(collection(db, 'virtualRooms'), (snapshot) => {
            const roomsData = [];
            snapshot.forEach((doc) => {
                roomsData.push({ id: doc.id, ...doc.data() });
            });
            setVirtualRooms(roomsData);
            updateProvidersFromRooms(roomsData);
        });

        return () => {
            unsubscribeMeetings();
            unsubscribeRooms();
        };
    }, []);

    // ── render ─────────────────────────────────────────────────────────────

    const shell = {
        minHeight: '100vh',
        position: 'relative',
        fontFamily: fontBody,
        color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    const statsData = [
        {
            label: 'Providers',
            value: providers.length,
            icon: <CloudIcon  key="config-1810-0-2" />,
            accent: palette.rust,
            hint: 'Configured',
        },
        {
            label: 'Active',
            value: providers.filter((p) => p.status === 'active').length,
            icon: <CheckCircleIcon  key="config-1817-1-2" />,
            accent: palette.pine,
            hint: 'Online',
        },
        {
            label: 'Meetings',
            value: summary.totalMeetings,
            icon: <VideocamIcon  key="config-1824-2-2" />,
            accent: palette.plum,
            hint: 'Total',
        },
        {
            label: 'Quality',
            value: `${summary.avgQuality}%`,
            icon: <VerifiedIcon  key="config-1831-3-2" />,
            accent: palette.teal,
            hint: 'Avg score',
        },
        {
            label: 'Storage',
            value: `${summary.totalStorage} GB`,
            icon: <StorageIcon  key="config-1838-4-2" />,
            accent: palette.amber,
            hint: 'Used',
        },
    ];

    if (loading) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.rust}>
                        <Box sx={{ display: 'grid', placeItems: 'center', gap: 2, py: 5 }}>
                            <CircularProgress sx={{ color: palette.rust }} />
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.88rem' }}>
                                Loading video settings...
                            </Typography>
                        </Box>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 2, sm: 3, lg: 5 },
                    py: { xs: 3, md: 4 },
                }}
            >
                {/* ── hero ──────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            mb: 3.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono,
                                    fontSize: '0.64rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.22em',
                                    textTransform: 'uppercase',
                                    color: palette.rust,
                                    mb: 0.75,
                                }}
                            >
                                Video Configuration
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em',
                                    lineHeight: 1.05,
                                    color: palette.ink,
                                }}
                            >
                                Video{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    settings
                                </Box>
                            </Typography>
                            <Typography
                                sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}
                            >
                                Manage video providers, quality settings, recordings, and analytics.
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.pine, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.pine, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <LiveIcon
                                        sx={{
                                            fontSize: 9,
                                            color: palette.pine,
                                            animation: 'tfPulse 2s ease-in-out infinite',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.pine,
                                        }}
                                    >
                                        {compact(summary.totalMeetings)} MEETINGS
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.teal, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.teal, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <VerifiedIcon sx={{ fontSize: 13, color: palette.teal }} />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.teal,
                                        }}
                                    >
                                        {summary.avgQuality}% QUALITY
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.plum, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.plum, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <StorageIcon sx={{ fontSize: 13, color: palette.plum }} />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.plum,
                                        }}
                                    >
                                        {summary.totalStorage} GB STORAGE
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => fetchAllData(true)}
                                disabled={refreshing}
                                startIcon={
                                    <RefreshIcon
                                        sx={{
                                            animation: refreshing ? 'tfSpin 1s linear infinite' : 'none',
                                        }}
                                    />
                                }
                                sx={{
                                    px: 2.25,
                                    py: 1,
                                    borderRadius: '14px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.82rem',
                                    color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                                onClick={handleOpenAddDialog}
                                startIcon={<AddIcon />}
                                sx={{
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: '14px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                    '&:hover': {
                                        background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})`,
                                    },
                                }}
                            >
                                Add Provider
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                    }}
                >
                    {statsData.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── tabs ──────────────────────────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.1} padding={{ xs: 1.75, md: 2.5 }}>
                    <Box sx={{ borderBottom: `1px solid ${palette.hairlineSoft}`, px: 1 }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.78rem',
                                    fontFamily: fontBody,
                                    color: palette.inkLight,
                                    minHeight: 44,
                                    px: 1.5,
                                    '&.Mui-selected': { color: palette.ink },
                                    '&:hover': { color: palette.rust },
                                },
                                '& .MuiTabs-indicator': {
                                    height: 3,
                                    borderRadius: 3,
                                    backgroundColor: tabs[tabValue]?.color || palette.rust,
                                },
                            }}
                        >
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={i}
                                    icon={React.cloneElement(tab.icon, { sx: { fontSize: 17 } })}
                                    iconPosition="start"
                                    label={tab.label}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ px: { xs: 0, sm: 1 } }}>
                        {/* ── PROVIDERS TAB ── */}
                        <TabPanel value={tabValue} index={0}>
                            <PanelHead
                                icon={<CloudIcon />}
                                title="Video Providers"
                                subtitle={`${compact(providers.length)} configured`}
                                accent={palette.rust}
                            />

                            {providers.length === 0 ? (
                                <EmptyState
                                    icon={<CloudIcon />}
                                    title="No providers configured"
                                    text="Add your first video provider to enable video meetings."
                                    action="Add Provider"
                                    onAction={handleOpenAddDialog}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 2.5,
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                    }}
                                >
                                    {providers.map((provider, index) => (
                                        <ProviderCard
                                            key={provider.id}
                                            provider={provider}
                                            index={index}
                                            onEdit={handleOpenEditDialog}
                                            onDelete={(id) => setDeleteConfirm({ open: true, providerId: id })}
                                            onTest={handleTestProvider}
                                        />
                                    ))}
                                </Box>
                            )}
                        </TabPanel>

                        {/* ── QUALITY TAB ── */}
                        <TabPanel value={tabValue} index={1}>
                            <PanelHead
                                icon={<VerifiedIcon />}
                                title="Quality Settings"
                                subtitle="Video and audio quality configuration"
                                accent={palette.pine}
                            />

                            <Alert
                                severity="info"
                                sx={{
                                    mb: 3,
                                    borderRadius: '12px',
                                    ...liquid(palette.teal, { depth: 0.5, radius: 12 }),
                                    border: `1px solid ${rgba(palette.teal, 0.2)}`,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: palette.ink }}>
                                    <strong>Note:</strong> Higher quality settings consume more bandwidth and CPU.
                                    Adjust based on your network conditions and device capabilities.
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'grid', gap: 3 }}>
                                {/* Quality Profiles */}
                                <GlassPanel accent={palette.rust} delay={0} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Quality Profiles
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 2,
                                            gridTemplateColumns: {
                                                xs: '1fr 1fr',
                                                sm: 'repeat(3, 1fr)',
                                                md: 'repeat(5, 1fr)',
                                            },
                                        }}
                                    >
                                        {Object.keys(QUALITY_PROFILES).map((key) => (
                                            <QualityProfileCard
                                                key={key}
                                                profile={key}
                                                selected={qualitySettings.defaultQuality === key.toLowerCase()}
                                                onSelect={(profile) => {
                                                    const q = QUALITY_PROFILES[profile.toUpperCase()];
                                                    setQualitySettings({
                                                        ...qualitySettings,
                                                        defaultQuality: profile.toLowerCase(),
                                                        videoBitrate: parseInt(q.bitrate) || 1500,
                                                        audioBitrate: parseInt(q.audioBitrate) || 96,
                                                        frameRate: q.frameRate || 30,
                                                    });
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </GlassPanel>

                                {/* Video Settings */}
                                <GlassPanel accent={palette.rust} delay={0.06} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        <VideocamIcon sx={{ color: palette.rust }} />
                                        Video Settings
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 2.5 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: palette.ink }}>
                                                Video Bitrate
                                            </Typography>
                                            <Slider
                                                value={qualitySettings.videoBitrate}
                                                onChange={(_, val) =>
                                                    setQualitySettings({ ...qualitySettings, videoBitrate: val })
                                                }
                                                min={100}
                                                max={8000}
                                                step={100}
                                                marks={[
                                                    { value: 200, label: '200' },
                                                    { value: 1500, label: '1.5M' },
                                                    { value: 3000, label: '3M' },
                                                    { value: 6000, label: '6M' },
                                                ]}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    '& .MuiSlider-track': { backgroundColor: palette.rust },
                                                    '& .MuiSlider-thumb': { backgroundColor: palette.rust },
                                                    '& .MuiSlider-markLabel': {
                                                        fontSize: '0.65rem',
                                                        color: palette.inkSoft,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box display="flex" gap={2}>
                                            <GlassSelect
                                                label="Video Codec"
                                                value={qualitySettings.videoCodec}
                                                onChange={(e) =>
                                                    setQualitySettings({
                                                        ...qualitySettings,
                                                        videoCodec: e.target.value,
                                                    })
                                                }
                                                options={VIDEO_CODECS.map((codec) => ({
                                                    value: codec.id,
                                                    label: codec.label,
                                                }))}
                                                color={palette.rust}
                                            />
                                            <GlassSelect
                                                label="Frame Rate"
                                                value={qualitySettings.frameRate}
                                                onChange={(e) =>
                                                    setQualitySettings({
                                                        ...qualitySettings,
                                                        frameRate: parseInt(e.target.value),
                                                    })
                                                }
                                                options={[
                                                    { value: 15, label: '15 fps' },
                                                    { value: 24, label: '24 fps' },
                                                    { value: 30, label: '30 fps' },
                                                    { value: 60, label: '60 fps' },
                                                ]}
                                                color={palette.rust}
                                            />
                                        </Box>
                                        <Box display="flex" flexWrap="wrap" gap={2}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableAdaptiveBitrate}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableAdaptiveBitrate: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Adaptive Bitrate"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableSimulcast}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableSimulcast: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Simulcast"
                                            />
                                        </Box>
                                    </Box>
                                </GlassPanel>

                                {/* Audio Settings */}
                                <GlassPanel accent={palette.plum} delay={0.12} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        <AudioIcon sx={{ color: palette.plum }} />
                                        Audio Settings
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 2.5 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: palette.ink }}>
                                                Audio Bitrate
                                            </Typography>
                                            <Slider
                                                value={qualitySettings.audioBitrate}
                                                onChange={(_, val) =>
                                                    setQualitySettings({ ...qualitySettings, audioBitrate: val })
                                                }
                                                min={16}
                                                max={256}
                                                step={16}
                                                marks={[
                                                    { value: 32, label: '32' },
                                                    { value: 64, label: '64' },
                                                    { value: 96, label: '96' },
                                                    { value: 128, label: '128' },
                                                ]}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    '& .MuiSlider-track': { backgroundColor: palette.plum },
                                                    '& .MuiSlider-thumb': { backgroundColor: palette.plum },
                                                    '& .MuiSlider-markLabel': {
                                                        fontSize: '0.65rem',
                                                        color: palette.inkSoft,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <GlassSelect
                                            label="Audio Codec"
                                            value={qualitySettings.audioCodec}
                                            onChange={(e) =>
                                                setQualitySettings({
                                                    ...qualitySettings,
                                                    audioCodec: e.target.value,
                                                })
                                            }
                                            options={AUDIO_CODECS.map((codec) => ({
                                                value: codec.id,
                                                label: codec.label,
                                            }))}
                                            color={palette.plum}
                                        />
                                        <Box display="flex" flexWrap="wrap" gap={2}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableDtx}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableDtx: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="DTX"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableAutomaticGainControl}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableAutomaticGainControl: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Auto Gain Control"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableEchoCancellation}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableEchoCancellation: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Echo Cancellation"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableNoiseSuppression}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableNoiseSuppression: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Noise Suppression"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={qualitySettings.enableStereo}
                                                        onChange={(e) =>
                                                            setQualitySettings({
                                                                ...qualitySettings,
                                                                enableStereo: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Stereo Audio"
                                            />
                                        </Box>
                                    </Box>
                                </GlassPanel>

                                {/* System Settings */}
                                <GlassPanel accent={palette.amber} delay={0.18} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        <SpeedIcon sx={{ color: palette.amber }} />
                                        System & Performance
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={qualitySettings.enableHardwareAcceleration}
                                                    onChange={(e) =>
                                                        setQualitySettings({
                                                            ...qualitySettings,
                                                            enableHardwareAcceleration: e.target.checked,
                                                        })
                                                    }
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: palette.pine,
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: palette.pine,
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Hardware Acceleration (GPU)"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={qualitySettings.enableLowLatency}
                                                    onChange={(e) =>
                                                        setQualitySettings({
                                                            ...qualitySettings,
                                                            enableLowLatency: e.target.checked,
                                                        })
                                                    }
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: palette.pine,
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: palette.pine,
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Low Latency Mode"
                                        />
                                    </Box>
                                </GlassPanel>

                                {/* Actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RestoreIcon />}
                                        onClick={resetQualitySettings}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: palette.amber,
                                            color: palette.amber,
                                            '&:hover': {
                                                borderColor: palette.amber,
                                                bgcolor: `${palette.amber}08`,
                                            },
                                        }}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveQualitySettings}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            py: 0.8,
                                            px: 3,
                                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 12px 28px -12px ${rgba(palette.rust, 0.8)}`,
                                            '&:hover': {
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 16px 36px -12px ${rgba(palette.rust, 0.9)}`,
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        Save Quality Settings
                                    </Button>
                                </Box>
                            </Box>
                        </TabPanel>

                        {/* ── RECORDINGS TAB ── */}
                        <TabPanel value={tabValue} index={2}>
                            <PanelHead
                                icon={<StorageIcon />}
                                title="Recording Settings"
                                subtitle="Manage recordings and storage"
                                accent={palette.teal}
                            />

                            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                {/* Recording Settings */}
                                <GlassPanel accent={palette.teal} delay={0} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Recording Configuration
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                        <GlassSelect
                                            label="Recording Type"
                                            value={recordingSettings.recordingType}
                                            onChange={(e) =>
                                                setRecordingSettings({
                                                    ...recordingSettings,
                                                    recordingType: e.target.value,
                                                })
                                            }
                                            options={Object.values(RECORDING_SETTINGS).map((setting) => ({
                                                value: setting.id,
                                                label: setting.label,
                                            }))}
                                            color={palette.teal}
                                        />

                                        <GlassSelect
                                            label="Recording Format"
                                            value={recordingSettings.recordingFormat}
                                            onChange={(e) =>
                                                setRecordingSettings({
                                                    ...recordingSettings,
                                                    recordingFormat: e.target.value,
                                                })
                                            }
                                            options={[
                                                { value: 'mp4', label: 'MP4' },
                                                { value: 'webm', label: 'WebM' },
                                                { value: 'mkv', label: 'MKV' },
                                                { value: 'aac', label: 'AAC (Audio)' },
                                            ]}
                                            color={palette.teal}
                                        />

                                        <Box display="flex" gap={2}>
                                            <GlassInput
                                                label="Max Retention Days"
                                                type="number"
                                                value={recordingSettings.maxRetentionDays}
                                                onChange={(e) =>
                                                    setRecordingSettings({
                                                        ...recordingSettings,
                                                        maxRetentionDays: parseInt(e.target.value),
                                                    })
                                                }
                                                icon={<TimerIcon sx={{ color: palette.amber }} />}
                                                color={palette.teal}
                                            />
                                            <GlassInput
                                                label="Storage Limit (GB)"
                                                type="number"
                                                value={recordingSettings.storageLimit}
                                                onChange={(e) =>
                                                    setRecordingSettings({
                                                        ...recordingSettings,
                                                        storageLimit: parseInt(e.target.value),
                                                    })
                                                }
                                                icon={<StorageIcon sx={{ color: palette.teal }} />}
                                                color={palette.teal}
                                            />
                                        </Box>

                                        <Divider sx={{ borderColor: palette.hairlineSoft }} />

                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={recordingSettings.autoRecord}
                                                        onChange={(e) =>
                                                            setRecordingSettings({
                                                                ...recordingSettings,
                                                                autoRecord: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Auto-record"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={recordingSettings.recordOnJoin}
                                                        onChange={(e) =>
                                                            setRecordingSettings({
                                                                ...recordingSettings,
                                                                recordOnJoin: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Record on Join"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={recordingSettings.includeChat}
                                                        onChange={(e) =>
                                                            setRecordingSettings({
                                                                ...recordingSettings,
                                                                includeChat: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Include Chat"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={recordingSettings.includeParticipants}
                                                        onChange={(e) =>
                                                            setRecordingSettings({
                                                                ...recordingSettings,
                                                                includeParticipants: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Include Participants"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={recordingSettings.compressRecordings}
                                                        onChange={(e) =>
                                                            setRecordingSettings({
                                                                ...recordingSettings,
                                                                compressRecordings: e.target.checked,
                                                            })
                                                        }
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.pine,
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: palette.pine,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label="Compress Recordings"
                                            />
                                        </Box>
                                    </Box>
                                </GlassPanel>

                                {/* Storage & Recent Recordings */}
                                <GlassPanel accent={palette.amber} delay={0.06} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Storage & Recent Recordings
                                    </Typography>

                                    <Box sx={{ mb: 3 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                                                Used Storage
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>
                                                {summary.totalStorage} GB / {recordingSettings.storageLimit} GB
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                height: 6,
                                                borderRadius: 4,
                                                bgcolor: palette.hairline,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{
                                                    width: `${Math.min(
                                                        (summary.totalStorage / recordingSettings.storageLimit) * 100,
                                                        100
                                                    )}%`,
                                                }}
                                                transition={{ duration: 0.8, ease: 'easeOut' }}
                                                style={{
                                                    height: '100%',
                                                    borderRadius: 4,
                                                    background: `linear-gradient(90deg, ${palette.rust}, ${palette.rust}80)`,
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: palette.ink }}>
                                        Recent Recordings
                                    </Typography>

                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: palette.canvasAlt }}>
                                                    <TableCell
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Name
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Date
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Duration
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Size
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Status
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ fontWeight: 700, color: palette.inkSoft, fontSize: '0.65rem' }}
                                                    >
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recordings.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={6}
                                                            align="center"
                                                            sx={{ py: 3, color: palette.inkSoft }}
                                                        >
                                                            No recordings found
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    recordings.slice(0, 5).map((recording) => (
                                                        <TableRow key={recording.id}>
                                                            <TableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={500}
                                                                    noWrap
                                                                    sx={{ color: palette.ink }}
                                                                >
                                                                    {recording.name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                                    {recording.date}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                                    {recording.duration}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                                    {recording.size}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <MetaPill
                                                                    label={recording.status || 'pending'}
                                                                    tone={
                                                                        recording.status === 'completed'
                                                                            ? palette.success
                                                                            : recording.status === 'processing'
                                                                            ? palette.warning
                                                                            : palette.danger
                                                                    }
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Box display="flex" gap={0.5} justifyContent="flex-end">
                                                                    {recording.status === 'completed' && (
                                                                        <Tooltip title="Download">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    handleDownloadRecording(recording)
                                                                                }
                                                                                sx={{
                                                                                    color: palette.teal,
                                                                                    '&:hover': {
                                                                                        bgcolor: `${palette.teal}12`,
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleDeleteRecording(recording.id)
                                                                            }
                                                                            sx={{
                                                                                color: palette.danger,
                                                                                '&:hover': {
                                                                                    bgcolor: `${palette.danger}12`,
                                                                                },
                                                                            }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </GlassPanel>

                                {/* Save Button */}
                                <Box sx={{ gridColumn: { md: '1 / -1' }, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveRecordingSettings}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            py: 0.8,
                                            px: 3,
                                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 12px 28px -12px ${rgba(palette.rust, 0.8)}`,
                                            '&:hover': {
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 16px 36px -12px ${rgba(palette.rust, 0.9)}`,
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        Save Recording Settings
                                    </Button>
                                </Box>
                            </Box>
                        </TabPanel>

                        {/* ── ANALYTICS TAB ── */}
                        <TabPanel value={tabValue} index={3}>
                            <PanelHead
                                icon={<TrendingUpIcon />}
                                title="Video Analytics"
                                subtitle="Live insights and metrics"
                                accent={palette.plum}
                                chip="Live"
                            />

                            {/* Summary Cards */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    mb: 3.5,
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                                }}
                            >
                                <StatCard
                                    label="Total Meetings"
                                    value={summary.totalMeetings}
                                    icon={<ScheduleIcon />}
                                    accent={palette.rust}
                                    hint="All time"
                                />
                                <StatCard
                                    label="Total Minutes"
                                    value={summary.totalMinutes}
                                    icon={<TimerIcon />}
                                    accent={palette.plum}
                                    hint="Meeting duration"
                                />
                                <StatCard
                                    label="Avg Participants"
                                    value={summary.avgParticipants}
                                    icon={<PeopleIcon />}
                                    accent={palette.pine}
                                    hint="Per meeting"
                                />
                                <StatCard
                                    label="Quality Score"
                                    value={`${summary.avgQuality}%`}
                                    icon={<VerifiedIcon />}
                                    accent={palette.teal}
                                    hint="Avg quality"
                                />
                                <StatCard
                                    label="Peak Concurrent"
                                    value={summary.peakConcurrent}
                                    icon={<GroupIcon />}
                                    accent={palette.amber}
                                    hint="Max online"
                                />
                            </Box>

                            {/* Time Range & Actions */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <GlassSelect
                                        label="Time Range"
                                        value={timeRange}
                                        onChange={(e) => setTimeRange(e.target.value)}
                                        options={[
                                            { value: 'week', label: 'Last 7 days' },
                                            { value: 'month', label: 'Last 30 days' },
                                            { value: 'quarter', label: 'Last 90 days' },
                                            { value: 'year', label: 'Last 12 months' },
                                        ]}
                                        color={palette.plum}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => fetchAllData(true)}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: palette.hairline,
                                            color: palette.ink,
                                            '&:hover': { borderColor: palette.rust, bgcolor: `${palette.rust}08` },
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<DownloadIcon />}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        py: 0.8,
                                        px: 3,
                                        background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 12px 28px -12px ${rgba(palette.rust, 0.8)}`,
                                        '&:hover': {
                                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 16px 36px -12px ${rgba(palette.rust, 0.9)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    Export Report
                                </Button>
                            </Box>

                            {/* Charts */}
                            <Box sx={{ display: 'grid', gap: 3 }}>
                                {/* Meeting Trends */}
                                <GlassPanel accent={palette.rust} delay={0} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Meeting Trends
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 225 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={analyticsData.meetingTrends}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 11, fill: palette.inkSoft }}
                                                />
                                                <YAxis tick={{ fontSize: 11, fill: palette.inkSoft }} />
                                                <ChartTooltip
                                                    contentStyle={{
                                                        borderRadius: '8px',
                                                        border: `1px solid ${palette.hairline}`,
                                                        boxShadow: shadows.sm,
                                                        padding: '8px 12px',
                                                        backgroundColor: palette.paper,
                                                    }}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="scheduled"
                                                    stackId="1"
                                                    stroke={palette.rust}
                                                    fill={palette.rust}
                                                    fillOpacity={0.6}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="completed"
                                                    stackId="1"
                                                    stroke={palette.pine}
                                                    fill={palette.pine}
                                                    fillOpacity={0.6}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="cancelled"
                                                    stackId="1"
                                                    stroke={palette.danger}
                                                    fill={palette.danger}
                                                    fillOpacity={0.4}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </GlassPanel>

                                {/* Quality Metrics */}
                                <GlassPanel accent={palette.pine} delay={0.06} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Quality Metrics
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 320 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={analyticsData.qualityMetrics}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 11, fill: palette.inkSoft }}
                                                />
                                                <YAxis
                                                    domain={[0, 100]}
                                                    tick={{ fontSize: 11, fill: palette.inkSoft }}
                                                />
                                                <ChartTooltip
                                                    contentStyle={{
                                                        borderRadius: '8px',
                                                        border: `1px solid ${palette.hairline}`,
                                                        boxShadow: shadows.sm,
                                                        padding: '8px 12px',
                                                        backgroundColor: palette.paper,
                                                    }}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                                <Line
                                                    type="monotone"
                                                    dataKey="video"
                                                    stroke={palette.rust}
                                                    strokeWidth={2}
                                                    dot={{ fill: palette.rust, r: 4 }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="audio"
                                                    stroke={palette.plum}
                                                    strokeWidth={2}
                                                    dot={{ fill: palette.plum, r: 4 }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="network"
                                                    stroke={palette.teal}
                                                    strokeWidth={2}
                                                    dot={{ fill: palette.teal, r: 4 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </GlassPanel>

                                {/* Usage Distribution & Participant Growth */}
                                <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                    <GlassPanel accent={palette.amber} delay={0.12} padding={{ xs: 2, md: 2.5 }}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                        >
                                            Feature Usage
                                        </Typography>
                                        <Box sx={{ width: '100%', height: 300 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={analyticsData.usageDistribution}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={85}
                                                        paddingAngle={4}
                                                        dataKey="value"
                                                        label={({ name, percent }) =>
                                                            `${name} ${(percent * 100).toFixed(0)}%`
                                                        }
                                                        labelLine={false}
                                                    >
                                                        {analyticsData.usageDistribution.map((_, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={COLORS[index % COLORS.length]}
                                                            />
                                                        ))}
                                                    </Pie>
                                                    <ChartTooltip
                                                        contentStyle={{
                                                            borderRadius: '8px',
                                                            border: `1px solid ${palette.hairline}`,
                                                            boxShadow: shadows.sm,
                                                            padding: '8px 12px',
                                                            backgroundColor: palette.paper,
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </GlassPanel>

                                    <GlassPanel accent={palette.plum} delay={0.18} padding={{ xs: 2, md: 2.5 }}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                        >
                                            Participant Growth
                                        </Typography>
                                        <Box sx={{ width: '100%', height: 300 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={analyticsData.participantGrowth}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: palette.inkSoft }}
                                                    />
                                                    <YAxis tick={{ fontSize: 11, fill: palette.inkSoft }} />
                                                    <ChartTooltip
                                                        contentStyle={{
                                                            borderRadius: '8px',
                                                            border: `1px solid ${palette.hairline}`,
                                                            boxShadow: shadows.sm,
                                                            padding: '8px 12px',
                                                            backgroundColor: palette.paper,
                                                        }}
                                                    />
                                                    <Bar
                                                        dataKey="participants"
                                                        fill={palette.rust}
                                                        radius={[4, 4, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </GlassPanel>
                                </Box>

                                {/* Live Status */}
                                <GlassPanel accent={palette.teal} delay={0.24} padding={{ xs: 2, md: 2.5 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 2, color: palette.ink, fontFamily: fontDisplay }}
                                    >
                                        Live Status
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 2,
                                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                                        }}
                                    >
                                        {[
                                            {
                                                label: 'Active Calls',
                                                value: virtualRooms.filter((r) => r.status === 'active').length,
                                                color: palette.pine,
                                                chip: 'Live',
                                            },
                                            {
                                                label: 'Participants Online',
                                                value: virtualRooms.reduce(
                                                    (acc, room) => acc + (room.currentParticipants || 0),
                                                    0
                                                ),
                                                color: palette.rust,
                                                chip: 'Active',
                                            },
                                            {
                                                label: 'Avg. Meeting Duration',
                                                value: summary.totalMeetings > 0
                                                    ? `${Math.round(summary.totalMinutes / summary.totalMeetings)} min`
                                                    : '0 min',
                                                color: palette.plum,
                                                chip: 'This Month',
                                            },
                                            {
                                                label: 'Network Quality',
                                                value: `${summary.avgQuality}%`,
                                                color: palette.teal,
                                                chip: 'Excellent',
                                            },
                                        ].map((item) => (
                                            <Box
                                                key={item.label}
                                                sx={{
                                                    p: 2,
                                                    textAlign: 'center',
                                                    borderRadius: '12px',
                                                    ...liquid(item.color, { depth: 0.5, radius: 12 }),
                                                    border: `1px solid ${rgba(item.color, 0.2)}`,
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: `0 8px 24px ${rgba(item.color, 0.1)}`,
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: palette.inkSoft,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.08em',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 700, color: item.color, mt: 0.5 }}
                                                >
                                                    {item.value}
                                                </Typography>
                                                <Chip
                                                    label={item.chip}
                                                    size="small"
                                                    sx={{
                                                        mt: 1,
                                                        fontWeight: 600,
                                                        bgcolor: item.color,
                                                        color: '#fff',
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </GlassPanel>
                            </Box>
                        </TabPanel>
                    </Box>
                </GlassPanel>
            </Container>

            {/* ── Dialogs ── */}

            {/* Provider Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.rust, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.rust, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon
                            icon={dialogMode === 'add' ? <AddIcon /> : <EditIcon />}
                            color={palette.rust}
                            size={46}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: palette.ink,
                                }}
                            >
                                {dialogMode === 'add' ? 'Add Video Provider' : 'Edit Provider'}
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                Configure video provider settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <GlassInput
                            label="Provider Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            icon={<CloudIcon sx={{ color: palette.rust }} />}
                            color={palette.rust}
                        />
                        <GlassSelect
                            label="Provider Type"
                            value={formData.type}
                            onChange={(e) =>
                                setFormData({ ...formData, type: e.target.value, config: {} })
                            }
                            options={providerTypes}
                            color={palette.rust}
                        />
                        <GlassSelect
                            label="Status"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            options={[
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                                { value: 'configuring', label: 'Configuring' },
                            ]}
                            color={palette.rust}
                        />

                        {formData.type && PROVIDER_CONFIGS && PROVIDER_CONFIGS[formData.type] && (
                            <>
                                <Alert
                                    severity="info"
                                    sx={{
                                        borderRadius: '12px',
                                        ...liquid(palette.teal, { depth: 0.5, radius: 12 }),
                                        border: `1px solid ${rgba(palette.teal, 0.2)}`,
                                    }}
                                >
                                    {PROVIDER_CONFIGS[formData.type].description}
                                </Alert>

                                {PROVIDER_CONFIGS[formData.type].fields &&
                                    Object.entries(PROVIDER_CONFIGS[formData.type].fields || {}).map(
                                        ([fieldName, fieldConfig]) => {
                                            const value = formData.config?.[fieldName] || fieldConfig.default || '';

                                            if (fieldConfig.type === 'select') {
                                                return (
                                                    <GlassSelect
                                                        key={fieldName}
                                                        label={fieldConfig.label}
                                                        value={value}
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                config: {
                                                                    ...formData.config,
                                                                    [fieldName]: e.target.value,
                                                                },
                                                            })
                                                        }
                                                        options={
                                                            fieldConfig.options
                                                                ? fieldConfig.options.map((opt) => ({
                                                                    value: opt,
                                                                    label: opt,
                                                                }))
                                                                : []
                                                        }
                                                        color={palette.rust}
                                                    />
                                                );
                                            }

                                            return (
                                                <GlassInput
                                                    key={fieldName}
                                                    label={fieldConfig.label}
                                                    value={value}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            config: {
                                                                ...formData.config,
                                                                [fieldName]: e.target.value,
                                                            },
                                                        })
                                                    }
                                                    type={fieldConfig.type || 'text'}
                                                    icon={<SecurityIcon sx={{ color: palette.inkSoft }} />}
                                                    helperText={fieldConfig.description || ''}
                                                    color={palette.rust}
                                                />
                                            );
                                        }
                                    )}

                                {PROVIDER_CONFIGS[formData.type].features &&
                                    PROVIDER_CONFIGS[formData.type].features.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                                sx={{ mb: 1, color: palette.ink }}
                                            >
                                                Available Features
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {PROVIDER_CONFIGS[formData.type].features.map((feature, index) => (
                                                    <MetaPill
                                                        key={index}
                                                        label={feature}
                                                        tone={palette.pine}
                                                        icon={<CheckIcon sx={{ fontSize: 12 }} />}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <Box
                    sx={{
                        px: 2.75,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.25,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                        background: rgba('#FFFFFF', 0.45),
                    }}
                >
                    <Button
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: palette.inkSoft,
                            borderRadius: '12px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSaveProvider}
                        startIcon={dialogMode === 'add' ? <AddIcon /> : <SaveIcon />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '14px',
                            px: 2.75,
                            py: 1,
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.rust}, ${rgba(palette.rust, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.rust, 0.95)}`,
                        }}
                    >
                        {dialogMode === 'add' ? 'Add Provider' : 'Save Changes'}
                    </Button>
                </Box>
            </Dialog>

            {/* Test Connection Dialog */}
            <Dialog
                open={testOpen}
                onClose={() => setTestOpen(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.teal, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.teal, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<NetworkIcon />} color={palette.teal} size={46} />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: palette.ink,
                                }}
                            >
                                Testing Connection
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                Verifying provider connectivity
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setTestOpen(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75 }}>
                    <Box sx={{ py: 2 }}>
                        {testStatus === 'testing' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <CircularProgress size={24} sx={{ color: palette.rust }} />
                                <Typography sx={{ color: palette.ink }}>Testing provider connection...</Typography>
                            </Box>
                        )}
                        {testStatus === 'success' && (
                            <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
                                Connection test completed successfully!
                            </Alert>
                        )}
                        {testStatus === 'error' && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                                Connection test failed. Please check your credentials.
                            </Alert>
                        )}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: palette.ink }}>
                                Test Logs
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: palette.canvasAlt,
                                    p: 2,
                                    borderRadius: 2,
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    border: `1px solid ${palette.hairline}`,
                                }}
                            >
                                {testLogs.map((log, index) => (
                                    <Typography
                                        key={index}
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: log.success ? palette.pine : palette.danger,
                                            fontFamily: fontMono,
                                            py: 0.5,
                                        }}
                                    >
                                        {log.success ? '✓' : '✗'} {log.message}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <Box
                    sx={{
                        px: 2.75,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                        background: rgba('#FFFFFF', 0.45),
                    }}
                >
                    <Button
                        onClick={() => setTestOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: palette.inkSoft,
                            borderRadius: '12px',
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, providerId: null })}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.danger, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.danger, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                        <GlassIcon icon={<DeleteIcon />} color={palette.danger} size={54} />
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: fontDisplay,
                            fontWeight: 600,
                            fontSize: '1.2rem',
                            color: palette.ink,
                        }}
                    >
                        Delete Provider?
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', mt: 1, mb: 2.75 }}>
                        This action cannot be undone. The provider will be permanently removed.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Button
                            fullWidth
                            onClick={() => setDeleteConfirm({ open: false, providerId: null })}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                color: palette.inkSoft,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            onClick={handleDeleteProvider}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#fff',
                                background: `linear-gradient(140deg, ${palette.danger}, ${rgba(palette.danger, 0.75)})`,
                                boxShadow: `0 12px 26px -12px ${rgba(palette.danger, 1)}`,
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default VideoSettings;